import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { AccountDialog } from "./AccountDialog";
import { useBackgroundRefresh } from "./useBackgroundRefresh";
import { useOverflowTooltip } from "./useOverflowTooltip";
import { loadPersistedBranding } from "../../branding/brandingStore";

const SIDEBAR_COLLAPSED_KEY = "yazamco.sidebar.collapsed";

/** Between the drawer breakpoint and this, the rail is always the 64px form. */
const AUTO_COLLAPSE_QUERY = "(min-width: 861px) and (max-width: 1024px)";

/** At or below this the rail leaves the flow and becomes an overlay drawer. */
const DRAWER_QUERY = "(max-width: 860px)";

function initialSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * True while the viewport is narrow enough that the rail must collapse on its
 * own. Kept separate from the stored preference: the user's choice is not
 * overwritten by resizing the window, it is only overridden while it does not
 * fit.
 */
function useMedia(query: string): boolean {
  const [narrow, setNarrow] = useState(
    () => window.matchMedia?.(query).matches ?? false,
  );

  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = (e: MediaQueryListEvent): void => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return narrow;
}

/**
 * Top-level application shell: the navigation rail and the content region,
 * both full height with no route title bar — the rail already says where you
 * are, and each screen owns its own heading.
 *
 * Below 860px the rail leaves the flow and becomes the canvas's 296px drawer
 * over a scrim, opened by the floating button each screen positions.
 */
export function MainLayout(): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
  const [accountOpen, setAccountOpen] = useState(false);
  const autoCollapsed = useMedia(AUTO_COLLAPSE_QUERY);
  // The drawer is the full-width rail sliding over the page; it always shows
  // its labels, so the collapsed preference does not apply there.
  const drawer = useMedia(DRAWER_QUERY);
  const location = useLocation();
  const isCopilot = location.pathname === "/copilot";
  const contentRef = useRef<HTMLElement>(null);
  useBackgroundRefresh();
  useOverflowTooltip(contentRef);

  useEffect(() => {
    void loadPersistedBranding().catch(() => {
      // Branding is presentation, not a reason to block the workspace. The
      // shipped identity remains active if the settings API is unavailable.
    });
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!drawer || !sidebarOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setSidebarOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawer, sidebarOpen]);

  const collapsed = !drawer && (sidebarCollapsed || autoCollapsed);

  return (
    <div className={`app-shell${collapsed ? " sidebar-collapsed" : ""}`}>
      <Sidebar
        open={sidebarOpen}
        collapsed={collapsed}
        onNavigate={() => setSidebarOpen(false)}
        onOpenAccount={() => setAccountOpen(true)}
        onToggleCollapse={() => {
          setSidebarCollapsed((current) => {
            const next = !current;
            try {
              window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
            } catch {
              // The preference is optional when browser storage is unavailable.
            }
            return next;
          });
        }}
      />
      <button
        className={`sidebar-backdrop${sidebarOpen ? " open" : ""}`}
        aria-label="Close navigation"
        onClick={() => setSidebarOpen(false)}
        aria-hidden={!sidebarOpen}
        tabIndex={sidebarOpen ? 0 : -1}
      />
      <main className={`main-content${isCopilot ? " copilot-content" : ""}`} ref={contentRef}>
        <button
          className="mobile-menu-button"
          type="button"
          onClick={() => setSidebarOpen((open) => !open)}
          aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={sidebarOpen}
        >
          <span aria-hidden />
        </button>
        <Outlet />
      </main>
      {accountOpen && <AccountDialog onClose={() => setAccountOpen(false)} />}
    </div>
  );
}
